/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { animated, useSpring } from '@react-spring/web';
import Blocks from '../blocks/Blocks';
import Countdown from '../blocks/Countdown';
import { useFloatingVideo } from '../../contexts/VideoContext';

import styles from '../../../styles/partials/career-section.module.scss';
import { Title } from '../blocks';

import * as AppPropTypes from '../../lib/PropTypes';
import Image from './Image';
import Detector from './Detector';

const propTypes = {
    item: PropTypes.shape({
        title: PropTypes.string,
        accentColor: PropTypes.string,
        image: AppPropTypes.image,
        backgroundImage: AppPropTypes.image,
        blocks: AppPropTypes.blocks,
    }),
    isPageHeader: PropTypes.bool,
    className: PropTypes.string,
    containerRef: AppPropTypes.ref,
};

const defaultProps = {
    item: null,
    isPageHeader: false,
    className: null,
    containerRef: null,
};

const CareerSection = ({ item, isPageHeader, className, containerRef }) => {
    const {
        title = null,
        accentColor = null,
        image = null,
        backgroundImage = null,
        backgroundColors = null,
        blocks = [],
        elementsColor = null,
    } = item || {};

    const finalBlocks = blocks.length > 0 ? blocks.filter(({ type }) => type !== 'countdown') : [];
    const titleColor = accentColor !== null ? { type: 'color', colors: [accentColor] } : null;

    const { setFloatingVideo } = useFloatingVideo();

    useEffect(() => {
        setFloatingVideo(null);
    }, [setFloatingVideo]);

    const [visible, setVisible] = useState(false);

    // MEMOIZE
    const countdownBlocks = useMemo(() => {
        if (blocks !== null && blocks.length > 0) {
            return blocks.reduce((filteredBlocks = [], block) => {
                if (block.type === 'countdown') {
                    filteredBlocks.push(block);
                }
                return filteredBlocks;
            }, []);
        }

        return [];
    }, [blocks]);

    // GRADIENT ONLY ON HERO SECTION
    // const sectionBackground = useMemo(() => {
    //     const { colors = [] } = backgroundColors || {};

    //     const backgroundColor =
    //         colors !== null && colors.length > 0 ? colors[colors.length - 1]?.color : null;

    //     return backgroundColor;
    // }, [backgroundColors]);

    // const sectionHeaderBackground = useMemo(() => {
    //     const { type, direction, colors = [] } = backgroundColors || {};
    //     const gradientDirection = direction === 'horizontal' ? 'to right' : 'to bottom';
    //     const colorStops = colors.map(({ color }, index) => {
    //         const position = Math.floor((index / (colors.length - 1)) * 100);
    //         return `${color} ${position}%`;
    //     });
    //     const backgroundImageCover =
    //         backgroundImage !== null ? `url(${backgroundImage.url})` : null;
    //     return {
    //         backgroundImage: ` ${backgroundImageCover}, ${type}-gradient(${gradientDirection}, ${colorStops.join(
    //             ', ',
    //         )})`,
    //     };
    // }, [backgroundColors]);

    const sectionBackground = useMemo(() => {
        const { type, direction, colors = [] } = backgroundColors || {};
        const gradientDirection = direction === 'horizontal' ? 'to right' : 'to bottom';
        const colorStops = colors.map(({ color }, index) => {
            const position = Math.floor((index / (colors.length - 1)) * 100);
            return `${color} ${position}%`;
        });
        return {
            backgroundImage: ` ${type}-gradient(${gradientDirection}, ${colorStops.join(', ')})`,
        };
    }, [backgroundColors]);

    const sectionHeaderBackground = useMemo(() => {
        const backgroundImageCover =
            backgroundImage !== null ? `url(${backgroundImage.url})` : null;
        return {
            backgroundImage: ` ${backgroundImageCover}`,
        };
    }, [backgroundColors]);

    const [{ transform, opacity }, api] = useSpring(() => ({
        transform: 'translateY(20%)',
        opacity: 0,
        config: { friction: 20, tension: 100, mass: 1 },
    }));

    useEffect(() => {
        if (visible) {
            api.start({
                transform: `translateY(0)`,
                opacity: 1,
            });
        } else {
            api.start({
                transform: `translateY(20%)`,
                opacity: 0,
            });
        }
    }, [visible]);

    return (
        <div
            ref={containerRef}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.blackText]: elementsColor === 'black',
                },
            ])}
            style={{ ...sectionBackground }}
        >
            <Detector rootMargin="-30%" onChange={(v) => setVisible(v)}>
                <div className={styles.frameContainer} style={{ ...sectionHeaderBackground }}>
                    {title !== null ? (
                        <div className={styles.title}>
                            <Title
                                body={title}
                                isContainer
                                colors={titleColor}
                                size={isPageHeader ? 'large' : 'medium'}
                            />
                        </div>
                    ) : null}
                    <animated.div
                        style={{
                            transform,
                            opacity,
                        }}
                    >
                        <Image media={image} className={styles.image} />
                    </animated.div>
                </div>
                <div
                    className={classNames([
                        styles.content,
                        {
                            [styles.withCountdown]:
                                countdownBlocks !== null && countdownBlocks.length > 0,
                        },
                    ])}
                >
                    {finalBlocks !== null ? (
                        <Blocks
                            className={styles.blocks}
                            blocks={finalBlocks.map((block) => {
                                const { type } = block;
                                return type === 'gallery'
                                    ? {
                                          ...block,
                                          imageClassName: styles.gallery,
                                      }
                                    : block;
                            })}
                            // isContainer
                            typeClassName={{
                                title: styles.title,
                            }}
                        />
                    ) : null}

                    {countdownBlocks !== null ? (
                        <div className={styles.countdownBlocks}>
                            {(countdownBlocks || []).map(
                                ({
                                    label: countdownLabel = null,
                                    endDate: countdownEndDate = null,
                                    link: countdownLink = {},
                                }) => (
                                    <Countdown
                                        label={countdownLabel}
                                        endDate={countdownEndDate}
                                        link={countdownLink}
                                        buttonColor={accentColor}
                                        className={styles.countdown}
                                    />
                                ),
                            )}
                        </div>
                    ) : null}
                </div>
            </Detector>
        </div>
    );
};

CareerSection.propTypes = propTypes;
CareerSection.defaultProps = defaultProps;

export default React.forwardRef((props, ref) => <CareerSection containerRef={ref} {...props} />);
